import React, { useState } from 'react';
import { Col, Row } from '@/lib/utils';
import { DemiLightText, MediumText, RegularText } from '@/lib/Text';
import { Button } from '@/components/ui/button';
import SvgIcon from '@/components/SvgIcon';
import { WeekSchedule, DAYS } from '../types/workingHours';
import { WorkToggle } from './WorkToggle';
import { TimeSelect } from './TimeSelect';
import { getCommonWorkTime, setCommonWorkTime, validateDaySchedule } from '../utils/workGridMappers';
import dayjs from 'dayjs';

type Props = {
    coachId: string;
    coachName: string;
    modified: boolean;
    weekSchedule: WeekSchedule;
    onChangeWeekSchedule: (next: WeekSchedule) => void;
    /** 다른 강사와 근무 데이터 스왑 */
    onSwap?: (srcCoachId: string, dstCoachId: string) => void;
};

const formatHhmm = (iso: string): string => {
    if (!iso) return '--:--';
    return dayjs(iso).format('HH:mm');
};

const dayColor = (day: (typeof DAYS)[number]): string => {
    if (day === '일') return '#D93043';
    if (day === '토') return '#3D73DD';
    return '#4B5563';
};

export const CoachRowHeader = ({
    coachId,
    coachName,
    modified,
    weekSchedule,
    onChangeWeekSchedule,
    onSwap,
}: Props) => {
    const [open, setOpen] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isBeingDragged, setIsBeingDragged] = useState(false);
    const common = getCommonWorkTime(weekSchedule);

    const swapEnabled = !!onSwap;

    const handleDragStart = (e: React.DragEvent) => {
        if (!swapEnabled) return;
        e.dataTransfer.setData('text/coach-id', coachId);
        e.dataTransfer.effectAllowed = 'move';
        setIsBeingDragged(true);
    };

    const handleDragEnd = () => {
        setIsBeingDragged(false);
        setIsDragOver(false);
    };

    const handleDragOver = (e: React.DragEvent) => {
        if (!swapEnabled) return;
        if (!e.dataTransfer.types.includes('text/coach-id')) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (!isDragOver) setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        if (!swapEnabled) return;
        e.preventDefault();
        const srcId = e.dataTransfer.getData('text/coach-id');
        setIsDragOver(false);
        if (!srcId || srcId === coachId) return;
        onSwap?.(srcId, coachId);
    };

    const displayStart = common === 'mixed' ? '혼합' : common ? formatHhmm(common.startTime) : '--:--';
    const displayEnd = common === 'mixed' ? '혼합' : common ? formatHhmm(common.endTime) : '--:--';

    // 다중 슬롯이 있는 요일 수 (행 헤더에 +N 표시용)
    const extraSlotCount = DAYS.reduce((acc, d) => {
        const s = weekSchedule[d];
        if (!s.enabled) return acc;
        const extra = Math.max(0, s.timeSlots.length - 1);
        return acc + extra;
    }, 0);

    const handleDayToggle = (day: (typeof DAYS)[number]) => {
        const cur = weekSchedule[day];
        const willEnable = !cur.enabled;
        onChangeWeekSchedule({
            ...weekSchedule,
            [day]: {
                enabled: willEnable,
                timeSlots: willEnable
                    ? cur.timeSlots.length > 0
                        ? cur.timeSlots
                        : [{ startTime: '', endTime: '' }]
                    : [],
            },
        });
    };

    const handleSlotChange = (
        day: (typeof DAYS)[number],
        slotIndex: number,
        field: 'startTime' | 'endTime',
        value: string,
    ) => {
        const cur = weekSchedule[day];
        const next = cur.timeSlots.map((slot, i) =>
            i === slotIndex ? { ...slot, [field]: value } : slot,
        );
        onChangeWeekSchedule({ ...weekSchedule, [day]: { ...cur, timeSlots: next } });
    };

    const handleAddSlot = (day: (typeof DAYS)[number]) => {
        const cur = weekSchedule[day];
        onChangeWeekSchedule({
            ...weekSchedule,
            [day]: {
                enabled: true,
                timeSlots: [...cur.timeSlots, { startTime: '', endTime: '' }],
            },
        });
    };

    const handleRemoveSlot = (day: (typeof DAYS)[number], slotIndex: number) => {
        const cur = weekSchedule[day];
        const remaining = cur.timeSlots.filter((_, i) => i !== slotIndex);
        onChangeWeekSchedule({
            ...weekSchedule,
            [day]: {
                enabled: remaining.length > 0,
                timeSlots: remaining,
            },
        });
    };

    const handleApplyCommon = (startTime: string, endTime: string) => {
        if (!startTime || !endTime) return;
        onChangeWeekSchedule(setCommonWorkTime(weekSchedule, startTime, endTime));
    };

    return (
        <>
            <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`h-[40px] w-[220px] min-w-[220px] pl-[12px] pr-[8px] flex items-center border-b border-r transition-colors select-none ${
                    isBeingDragged
                        ? 'opacity-50 bg-[#F2F4F6] border-[#E6E8EA]'
                        : isDragOver
                          ? 'bg-[#EEF3FC] border-[#3D73DD]'
                          : 'border-[#E6E8EA]'
                }`}
            >
                {/* 이름 영역 — 클릭으로 요일/시간 모달 */}
                <div
                    className="flex-1 min-w-0 cursor-pointer hover:bg-[#F7F8FA] rounded-[6px] px-[4px] py-[2px] transition-colors"
                    onClick={() => setOpen(true)}
                >
                    <Row className="items-center gap-[6px]">
                        <RegularText fontSize={14} color="#3D4244">
                            {coachName}
                        </RegularText>
                        {modified && <div className="w-[6px] h-[6px] bg-[#3D73DD] rounded-full" />}
                    </Row>
                </div>

                {/* 시간 영역 — 클릭으로 시간 설정, 드래그로 다른 강사와 교환 */}
                <div
                    draggable={swapEnabled}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onClick={() => setOpen(true)}
                    title={
                        swapEnabled
                            ? '클릭해서 시간 설정 · 드래그해서 다른 강사와 바꾸기'
                            : '클릭해서 시간 설정'
                    }
                    className={`flex items-center gap-[4px] rounded-[6px] px-[6px] py-[4px] transition-colors cursor-pointer hover:bg-[#EEF3FC] ${
                        swapEnabled ? 'active:cursor-grabbing' : ''
                    }`}
                >
                    {swapEnabled && <SwapIcon />}
                    <Row className="items-center gap-[2px]">
                        <DemiLightText fontSize={13} color="#6B7280">
                            {displayStart}
                        </DemiLightText>
                        <DemiLightText fontSize={13} color="#6B7280">
                            ~
                        </DemiLightText>
                        <DemiLightText fontSize={13} color="#6B7280">
                            {displayEnd}
                        </DemiLightText>
                        {extraSlotCount > 0 && (
                            <span className="ml-[4px] inline-flex items-center justify-center px-[5px] min-h-[20px] rounded-[4px] bg-[#EEF3FC] text-[#3D73DD] text-[13px] leading-none font-semibold">
                                +{extraSlotCount}
                            </span>
                        )}
                    </Row>
                </div>
            </div>

            {/* 중앙 모달 */}
            {open && (
                <div
                    className="fixed inset-0 z-[99998] bg-black/30 flex items-center justify-center"
                    onClick={(e) => {
                        if (e.currentTarget === e.target) setOpen(false);
                    }}
                >
                    <div
                        className="bg-white rounded-[20px] shadow-lg w-[580px] max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Col className="p-[24px]">
                            <Row className="items-center justify-between mb-[4px]">
                                <MediumText fontSize={20} color="#1A1A1A">
                                    {coachName}
                                </MediumText>
                                <div className="cursor-pointer p-[6px]" onClick={() => setOpen(false)}>
                                    <SvgIcon name="SvgX" size={14} stroke="#6B7280" strokeWidth={2} />
                                </div>
                            </Row>
                            <RegularText fontSize={13} color="#6B7280" className="mb-[16px]">
                                요일별 근무 여부 및 시간을 설정할 수 있어요
                            </RegularText>

                            <CommonTimeApply onApply={handleApplyCommon} />

                            <div className="flex gap-[18px] flex-col mt-[16px] overflow-y-auto pr-[8px] max-h-[480px]">
                                {DAYS.map((day) => {
                                    const s = weekSchedule[day];
                                    const slots = s.timeSlots;
                                    const errors = validateDaySchedule(s);
                                    const errorBySlot = new Map<number, string>();
                                    errors.forEach((e) => {
                                        if (!errorBySlot.has(e.slotIndex)) errorBySlot.set(e.slotIndex, e.message);
                                    });
                                    return (
                                        <Row key={day} className="items-start">
                                            <RegularText
                                                fontSize={15}
                                                color={dayColor(day)}
                                                className="w-[30px] text-center pt-[10px]"
                                            >
                                                {day}
                                            </RegularText>
                                            <div className="w-[60px] flex justify-center pt-[10px]">
                                                <WorkToggle value={s.enabled} onToggle={() => handleDayToggle(day)} />
                                            </div>
                                            <Col className="ml-[8px] gap-[8px]">
                                                {!s.enabled || slots.length === 0 ? (
                                                    <Row className="items-center">
                                                        <TimeSelect
                                                            value=""
                                                            onChange={() => {}}
                                                            disabled
                                                            compact
                                                            placeholder="시작"
                                                        />
                                                        <MediumText
                                                            fontSize={16}
                                                            color="#6B7280"
                                                            className="mx-[8px]"
                                                        >
                                                            ~
                                                        </MediumText>
                                                        <TimeSelect
                                                            value=""
                                                            onChange={() => {}}
                                                            disabled
                                                            compact
                                                            placeholder="종료"
                                                        />
                                                    </Row>
                                                ) : (
                                                    slots.map((slot, idx) => {
                                                        const slotErr = errorBySlot.get(idx);
                                                        const isEmptyErr = !slot.startTime || !slot.endTime;
                                                        return (
                                                            <Col key={idx} className="gap-[2px]">
                                                                <Row className="items-center">
                                                                    <TimeSelect
                                                                        value={slot.startTime}
                                                                        onChange={(val) =>
                                                                            handleSlotChange(day, idx, 'startTime', val)
                                                                        }
                                                                        disabled={false}
                                                                        compact
                                                                        placeholder="시작"
                                                                    />
                                                                    <MediumText
                                                                        fontSize={16}
                                                                        color="#6B7280"
                                                                        className="mx-[8px]"
                                                                    >
                                                                        ~
                                                                    </MediumText>
                                                                    <TimeSelect
                                                                        value={slot.endTime}
                                                                        onChange={(val) =>
                                                                            handleSlotChange(day, idx, 'endTime', val)
                                                                        }
                                                                        disabled={false}
                                                                        compact
                                                                        allowDayEnd
                                                                        placeholder="종료"
                                                                    />
                                                                    <div
                                                                        onClick={() => handleRemoveSlot(day, idx)}
                                                                        className="w-[24px] h-[24px] ml-[8px] rounded-full bg-[#F2F4F6] hover:bg-[#E6E8EA] flex items-center justify-center cursor-pointer"
                                                                        title="구간 삭제"
                                                                    >
                                                                        <SvgIcon
                                                                            name="SvgX"
                                                                            size={9}
                                                                            stroke="#6B7280"
                                                                            strokeWidth={2}
                                                                        />
                                                                    </div>
                                                                </Row>
                                                                {slotErr && !isEmptyErr && (
                                                                    <RegularText
                                                                        fontSize={13}
                                                                        color="#D93043"
                                                                        className="ml-[4px]"
                                                                    >
                                                                        {slotErr}
                                                                    </RegularText>
                                                                )}
                                                            </Col>
                                                        );
                                                    })
                                                )}
                                                {s.enabled && (
                                                    <Row
                                                        className="items-center cursor-pointer w-fit gap-[6px]"
                                                        onClick={() => handleAddSlot(day)}
                                                    >
                                                        <SvgIcon name="SvgPlus" size={12} fill="#3D73DD" />
                                                        <RegularText fontSize={13} color="#3D73DD">
                                                            시간 구간 추가
                                                        </RegularText>
                                                    </Row>
                                                )}
                                            </Col>
                                        </Row>
                                    );
                                })}
                            </div>

                            <Row className="justify-end mt-[20px]">
                                <Button
                                    variant="default"
                                    className="h-[40px] px-[20px] rounded-lg bg-[#3D73DD] text-white hover:bg-[#3D73DD]/90"
                                    onClick={() => setOpen(false)}
                                >
                                    <RegularText fontSize={14} color="#FFFFFF">
                                        확인
                                    </RegularText>
                                </Button>
                            </Row>
                        </Col>
                    </div>
                </div>
            )}
        </>
    );
};

const CommonTimeApply = ({ onApply }: { onApply: (start: string, end: string) => void }) => {
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    return (
        <Row className="items-center gap-[8px] bg-[#F7F8FA] rounded-[12px] p-[10px]">
            <DemiLightText fontSize={13} color="#6B7280" className="w-[80px]">
                시간 일괄 적용
            </DemiLightText>
            <TimeSelect value={start} onChange={setStart} disabled={false} compact placeholder="시작" />
            <DemiLightText fontSize={14} color="#6B7280">
                ~
            </DemiLightText>
            <TimeSelect value={end} onChange={setEnd} disabled={false} compact allowDayEnd placeholder="종료" />
            <Button
                variant="ghost"
                className={`h-[36px] px-[14px] rounded-lg ml-auto ${
                    start && end
                        ? 'bg-[#3D73DD] text-white hover:bg-[#3D73DD]/90'
                        : 'bg-[#3D73DD] text-white opacity-40 cursor-not-allowed'
                }`}
                onClick={
                    start && end
                        ? () => {
                              onApply(start, end);
                              setStart('');
                              setEnd('');
                          }
                        : undefined
                }
            >
                <RegularText fontSize={13} color="#FFFFFF">
                    적용
                </RegularText>
            </Button>
        </Row>
    );
};

/** 강사 간 근무 시간 스왑 표시용 ⇅ 아이콘 */
const SwapIcon = () => (
    <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        className="shrink-0"
        aria-hidden="true"
    >
        <path
            d="M3.5 2V9.5M3.5 2L1.8 3.7M3.5 2L5.2 3.7"
            stroke="#6B7280"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M8.5 10V2.5M8.5 10L10.2 8.3M8.5 10L6.8 8.3"
            stroke="#6B7280"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);
